import styled from "styled-components";
import useOwnerStats from "features/owner/hooks/useOwnerStats";
import Refresh from "shared/components/Refresh";
import LoadingSpinner from "shared/components/Spinner";
import { hw, scrollbar } from "features/homework/homeworkUi";

const formatAmount = (value: number) => value.toLocaleString("ko-KR");

const Stats = () => {
    const { stats, isLoading, isError, onRefresh } = useOwnerStats();
    const maxStudents = Math.max(...stats.branches.map(item => item.activeStudentCount), 1);
    const maxScore = Math.max(...stats.branches.map(item => item.monthScore), 1);
    const now = new Date();
    const monthLabel = `${now.getFullYear()}년 ${now.getMonth() + 1}월`;

    const kpis = [
        { label: "지점", value: `${stats.branchCount}곳` },
        { label: "강사", value: `${stats.teacherCount}명` },
        { label: "재원생", value: `${stats.activeStudentCount}명` },
        { label: "배포중 과제", value: `${stats.activeReleaseCount}개` },
        { label: "미납 인원", value: `${stats.unpaidCount}명` },
        { label: "미납 금액", value: `${formatAmount(stats.unpaidAmount)}원` },
    ];

    const statusRows = [
        { label: "재원", value: stats.activeStudentCount, color: hw.success },
        { label: "휴원", value: stats.suspendedStudentCount, color: hw.warning },
        { label: "졸업", value: stats.graduatedStudentCount, color: hw.textSubtle },
    ];
    const statusTotal = Math.max(stats.studentCount, 1);

    const permissionRows = [
        { label: "단어", value: stats.wordBranchCount },
        { label: "문장", value: stats.sentenceBranchCount },
        { label: "영상", value: stats.videoBranchCount },
    ];
    const permissionTotal = Math.max(stats.branchCount, 1);

    return (
        <Page>
            <PageHeader>
                <div>
                    <Title>통계</Title>
                    <Desc>전체 지점의 원생·강사·과제 배포·미납 현황을 한눈에 확인합니다. ({monthLabel} 기준)</Desc>
                </div>
                <Refresh onRefreshClick={onRefresh} />
            </PageHeader>
            {isLoading ? (
                <LoadingWrap>
                    <LoadingSpinner width={72} />
                </LoadingWrap>
            ) : isError ? (
                <Empty>통계를 불러오지 못했습니다. 새로고침 후 다시 시도해 주세요.</Empty>
            ) : (
                <Body>
                    <KpiGrid>
                        {kpis.map(item => (
                            <Kpi key={item.label}>
                                <KpiLabel>{item.label}</KpiLabel>
                                <KpiValue>{item.value}</KpiValue>
                            </Kpi>
                        ))}
                    </KpiGrid>
                    <Split>
                        <Card>
                            <CardTitle>원생 상태</CardTitle>
                            <CardDesc>전체 {stats.studentCount}명 기준</CardDesc>
                            {statusRows.map(row => (
                                <BarRow key={row.label}>
                                    <BarMeta>
                                        <span>{row.label}</span>
                                        <strong>{row.value}명</strong>
                                    </BarMeta>
                                    <Track>
                                        <Fill $color={row.color} $ratio={row.value / statusTotal} />
                                    </Track>
                                </BarRow>
                            ))}
                        </Card>
                        <Card>
                            <CardTitle>과제 권한 이용 지점</CardTitle>
                            <CardDesc>단어·문장·영상 권한이 켜진 지점 수</CardDesc>
                            {permissionRows.map(row => (
                                <BarRow key={row.label}>
                                    <BarMeta>
                                        <span>{row.label}</span>
                                        <strong>{row.value}곳</strong>
                                    </BarMeta>
                                    <Track>
                                        <Fill $color={hw.brand} $ratio={row.value / permissionTotal} />
                                    </Track>
                                </BarRow>
                            ))}
                        </Card>
                    </Split>
                    <Card>
                        <CardTitle>지점별 재원생</CardTitle>
                        <CardDesc>재원 중인 학생 수 기준</CardDesc>
                        {stats.branches.length ? stats.branches.map(item => (
                            <BarRow key={`student-${item.id}`}>
                                <BarMeta>
                                    <span>{item.name}</span>
                                    <strong>{item.activeStudentCount}명</strong>
                                </BarMeta>
                                <Track>
                                    <Fill $color={hw.brand} $ratio={item.activeStudentCount / maxStudents} />
                                </Track>
                            </BarRow>
                        )) : <Empty>등록된 지점이 없습니다.</Empty>}
                    </Card>
                    <Card>
                        <CardTitle>지점별 이달 학습 점수</CardTitle>
                        <CardDesc>TossScore {monthLabel} 합계</CardDesc>
                        {stats.branches.length ? stats.branches.map(item => (
                            <BarRow key={`score-${item.id}`}>
                                <BarMeta>
                                    <span>{item.name}</span>
                                    <strong>{formatAmount(item.monthScore)}점</strong>
                                </BarMeta>
                                <Track>
                                    <Fill $color={hw.text} $ratio={item.monthScore / maxScore} />
                                </Track>
                            </BarRow>
                        )) : <Empty>등록된 지점이 없습니다.</Empty>}
                    </Card>
                    <Card>
                        <CardHead>
                            <div>
                                <CardTitle>지점별 상세</CardTitle>
                                <CardDesc>강사·학급·배포중 과제·미납을 지점 단위로 비교합니다. 전체 공지 {stats.noticeCount}건</CardDesc>
                            </div>
                            <Count>{stats.branches.length}곳</Count>
                        </CardHead>
                        <TableWrap>
                            <Table>
                                <thead>
                                    <tr>
                                        <th>지점</th>
                                        <th>강사</th>
                                        <th>재원생</th>
                                        <th>학급</th>
                                        <th>배포중</th>
                                        <th>미납</th>
                                        <th>미납액</th>
                                        <th>이달 점수</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.branches.length ? stats.branches.map(item => (
                                        <tr key={item.id}>
                                            <td><strong>{item.name}</strong></td>
                                            <td>{item.teacherCount}</td>
                                            <td>{item.activeStudentCount}</td>
                                            <td>{item.lessonCount}</td>
                                            <td>{item.activeReleaseCount}</td>
                                            <td>{item.unpaidCount}</td>
                                            <td>{formatAmount(item.unpaidAmount)}</td>
                                            <td>{formatAmount(item.monthScore)}</td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan={8}>등록된 지점이 없습니다.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </TableWrap>
                    </Card>
                </Body>
            )}
        </Page>
    );
};

const Page = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: 20px 24px 32px;
    ${scrollbar}
`;

const PageHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    flex-shrink: 0;
`;

const Title = styled.h1`
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.03em;
    color: ${hw.text};
`;

const Desc = styled.p`
    margin: 4px 0 0;
    font-size: 14px;
    color: ${hw.textMuted};
`;

const LoadingWrap = styled.div`
    flex: 1;
    min-height: 280px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const KpiGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 12px;

    @media (max-width: 1200px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @media (max-width: 720px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const Kpi = styled.div`
    padding: 16px 18px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    border-radius: 16px;
    box-shadow: ${hw.shadow};
`;

const KpiLabel = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: ${hw.textMuted};
`;

const KpiValue = styled.div`
    margin-top: 8px;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.03em;
    color: ${hw.text};
`;

const Split = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @media (max-width: 960px) {
        grid-template-columns: 1fr;
    }
`;

const Card = styled.section`
    padding: 18px 20px 16px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    border-radius: 16px;
    box-shadow: ${hw.shadow};
    min-width: 0;
`;

const CardHead = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    margin-bottom: 12px;
`;

const CardTitle = styled.h2`
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: ${hw.text};
    letter-spacing: -0.02em;
`;

const CardDesc = styled.p`
    margin: 4px 0 14px;
    font-size: 13px;
    color: ${hw.textMuted};
`;

const Count = styled.span`
    font-size: 12px;
    font-weight: 700;
    color: ${hw.brand};
    background: ${hw.brandSoft};
    padding: 4px 10px;
    border-radius: 999px;
    flex-shrink: 0;
`;

const BarRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 12px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const BarMeta = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
    color: ${hw.textMuted};

    span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: ${hw.text};
        font-weight: 600;
    }
`;

const Track = styled.div`
    height: 8px;
    background: ${hw.bg};
    border: 1px solid ${hw.border};
    border-radius: 999px;
    overflow: hidden;
`;

const Fill = styled.div<{ $color: string; $ratio: number }>`
    height: 100%;
    width: ${props => `${Math.max(0, Math.min(1, props.$ratio)) * 100}%`};
    background: ${props => props.$color};
`;

const TableWrap = styled.div`
    overflow: auto;
    ${scrollbar}
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    min-width: 720px;

    th, td {
        padding: 12px 10px;
        text-align: right;
        font-size: 13px;
        border-bottom: 1px solid ${hw.border};
        color: ${hw.text};
        white-space: nowrap;
    }

    th {
        text-align: right;
        color: ${hw.textMuted};
        font-weight: 700;
        background: #FAFBFC;
        position: sticky;
        top: 0;
    }

    th:first-child,
    td:first-child {
        text-align: left;
    }

    tbody tr:hover {
        background: ${hw.brandSoft};
    }
`;

const Empty = styled.div`
    padding: 28px 8px;
    text-align: center;
    color: ${hw.textSubtle};
    font-size: 13px;
`;

export default Stats;
